import React from 'react';
import { EyeSlashIcon, PhotoIcon, DocumentIcon } from '@heroicons/react/24/outline';

const ComplaintDetailsTab = ({
  complaint,
  getStatusColor,
  getPriorityColor,
  formatComplaintType,
  formatCategory,
  formatStatus
}) => {
  const getComplainantName = () => {
    if (complaint.is_anonymous) return 'Anonymous';
    if (complaint.resident) {
      if (typeof complaint.resident === 'string') return complaint.resident;
      if (typeof complaint.resident === 'object' && complaint.resident.name) {
        return complaint.resident.name;
      }
    }
    return complaint.complainant_name || 'N/A';
  };

  const getCategoryName = () => {
    if (!complaint.category) return 'N/A';
    if (typeof complaint.category === 'string') return complaint.category;
    if (typeof complaint.category === 'object') {
      return complaint.category.name || 'Uncategorized';
    }
    return 'N/A';
  };

  const getPriority = () => {
    if (complaint.priority) return complaint.priority;
    if (complaint.category && typeof complaint.category === 'object') {
      return complaint.category.priority || 'low';
    }
    return 'low';
  };

  const getStatus = () => {
    return complaint.status || 'submitted';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-gray-50 rounded-lg p-4 space-y-4">
        {/* First Row - Complainant and Purok */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Complainant Name
            </label>
            <p className="text-sm text-gray-900">
              {getComplainantName()}
            </p>
           
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Purok
            </label>
            <p className="text-sm text-gray-900">
              {complaint.purok || 'N/A'}
            </p>
          </div>
        </div>

        {/* Second Row - Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <p className="text-sm text-gray-900">
            {complaint.description || 'N/A'}
          </p>
        </div>

        {/* Third Row - Category and Priority */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Complaint Category
            </label>
            <p className="text-sm text-gray-900">
              {getCategoryName()}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Priority Level
            </label>
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(getPriority())}`}>
              {getPriority()}
            </span>
          </div>
        </div>

        {/* Fourth Row - Status and Landmark */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(getStatus())}`}>
              {formatStatus(getStatus())}
            </span>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Landmark
            </label>
            <p className="text-sm text-gray-900">
              {complaint.landmark || 'N/A'}
            </p>
          </div>
        </div>

        {/* Created and Updated Dates */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2 border-t border-gray-200">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Created At
            </label>
            <p className="text-sm text-gray-900">
              {formatDate(complaint.created_at)}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Updated At
            </label>
            <p className="text-sm text-gray-900">
              {formatDate(complaint.updated_at)}
            </p>
          </div>
        </div>
      </div>

      {/* Attachments Section */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Attachments
        </label>
        
        {complaint.attachments && complaint.attachments.length > 0 ? (
          <div className="space-y-3">
            {complaint.attachments.map((attachment, index) => (
              <div key={attachment.attachment_id || index} className="flex items-center p-3 border border-gray-200 rounded-md hover:bg-gray-50">
                {attachment.file_type === 'pdf' ? (
                  <DocumentIcon className="h-5 w-5 text-red-400 mr-2" />
                ) : (
                  <PhotoIcon className="h-5 w-5 text-blue-400 mr-2" />
                )}
                <a 
                  href={attachment.file_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 underline hover:text-blue-800 flex-1"
                >
                  {attachment.file_name}
                </a>
                <span className="text-xs text-gray-500">
                  {(attachment.file_size / 1024).toFixed(2)} KB
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">
            No attachments
          </p>
        )}
      </div>

      {/* Anonymous Submission */}
      {complaint.is_anonymous && (
        <div className="mt-4 flex items-center gap-2 text-gray-600">
          <EyeSlashIcon className="w-4 h-4" />
          <span className="text-sm">Anonymous Submission</span>
        </div>
      )}
    </div>
  );
};

export default ComplaintDetailsTab;